import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * Aviso de que una de las dos cuentas se quedó sin saldo.
 *
 * xVision consume Anthropic (el chat) y fal.ai (la generación). Son cuentas
 * separadas, con saldos separados. El 2026-09-04 Anthropic se quedó en cero:
 * nadie se enteró hasta que una operadora se chocó un JSON crudo en medio de
 * un trabajo, y se terminó recargando fal.ai —que tenía US$73— porque el error
 * no decía cuál era. Este cartel existe para que eso se vea ANTES de escribir
 * el prompt, y para que diga con todas las letras cuál hay que recargar.
 */

type State = {
  status: 'ok' | 'low' | 'down' | 'unknown';
  detail?: string;
  checkedAt: string | null;
};
type Health = { anthropic: State; fal: State & { balanceUsd: number | null } };

export function ProviderAlert() {
  const { data } = useQuery({
    queryKey: ['providers-health'],
    queryFn: () => api.get<Health>('/providers/health'),
    refetchInterval: 60_000,
    // Que un fallo del chequeo no meta ruido: si no sabemos, no avisamos.
    retry: false,
  });
  if (!data) return null;

  const avisos: Array<{ nombre: string; detalle: string; donde: string; grave: boolean }> = [];

  if (data.anthropic.status === 'down') {
    avisos.push({
      nombre: 'Anthropic',
      detalle: data.anthropic.detail ?? 'La cuenta de Anthropic no está respondiendo.',
      donde: 'console.anthropic.com → Plans & Billing',
      grave: true,
    });
  }
  if (data.fal.status === 'down' || data.fal.status === 'low') {
    avisos.push({
      nombre: 'fal.ai',
      detalle: data.fal.detail ?? 'Revisar el saldo de fal.ai.',
      donde: 'fal.ai/dashboard/billing',
      grave: data.fal.status === 'down',
    });
  }
  if (avisos.length === 0) return null;

  return (
    <div className="shrink-0">
      {avisos.map((a) => (
        <div
          key={a.nombre}
          className={
            'px-3 py-2 text-xs sm:text-[13px] border-b flex flex-wrap items-baseline gap-x-2 gap-y-1 ' +
            (a.grave
              ? 'bg-red-950/60 border-red-900 text-red-100'
              : 'bg-amber-950/50 border-amber-900 text-amber-100')
          }
        >
          <span>{a.grave ? '⛔' : '⚠️'}</span>
          <strong>{a.detalle}</strong>
          <span className="opacity-80">
            {a.grave
              ? `Hasta que la recarguen, ${a.nombre === 'Anthropic' ? 'el chat no responde' : 'no se puede generar'}. No es un error de tu pedido.`
              : 'Conviene recargar antes de arrancar una tanda larga.'}
          </span>
          <span className="opacity-70">
            Se recarga en <span className="font-mono">{a.donde}</span> — es la cuenta de{' '}
            <strong>{a.nombre}</strong>, recargar la otra no destraba nada.
          </span>
        </div>
      ))}
    </div>
  );
}
